import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { getDocumentHierarchicalStructure } from '../api/word/word-service'
import { WORKING_DOCUMENT_ID } from '../constants/files'
import type { DocumentSectionTree } from '../types'
import { useSelectedContext } from './useSelectedContext'

function collectSectionIds(nodes: DocumentSectionTree[], ids = new Set<string>()): Set<string> {
  for (const node of nodes) {
    ids.add(node.id)
    collectSectionIds(node.children, ids)
  }
  return ids
}

export function useGetWorkingDocumentHierarchicalStructure() {
  const { getFileSelection, updateFileSelection } = useSelectedContext()

  const query = useQuery({
    queryKey: ['WorkingDocumentHierarchicalStructure'],
    queryFn: getDocumentHierarchicalStructure,
    staleTime: 5000, // 5 seconds
  })

  // When the document structure refreshes, prune any selected sections that no longer exist
  useEffect(() => {
    if (!query.data) return

    const workingDoc = getFileSelection(WORKING_DOCUMENT_ID)
    if (!workingDoc?.sections?.length) return

    const existingIds = collectSectionIds(query.data)
    const remainingSections = workingDoc.sections.filter((s) => existingIds.has(s.id))
    if (remainingSections.length === workingDoc.sections.length) return

    updateFileSelection({
      ...workingDoc,
      sections: remainingSections.length > 0 ? remainingSections : null,
    })
  }, [query.data, getFileSelection, updateFileSelection])

  return query
}
